import { ShieldCheck, Building2, Banknote, Database, Target, Gauge, Zap } from "lucide-react";
import type { IconCardGridComponent } from "../../types";
import { JSX } from "react";

const COLS: Record<number, string> = {
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

function toPerRow(v: unknown): 2 | 3 | 4 {
  const n = Number(v);
  return n >= 2 && n <= 4 ? (n as 2 | 3 | 4) : 2;
}

function Card({
  icon,
  title,
  text,
  iconMap,
}: {
  icon: string;
  title: string;
  text: string;
  iconMap: Record<string, JSX.Element>;
}) {
  return (
    <div className="apa-card apa-card-hover group h-full p-6 md:p-7">
      <div className="flex h-full flex-col items-center text-center">
        <span className="grid h-12 w-12 place-items-center rounded-xl bg-[var(--color-forest-100)] text-[var(--color-forest-600)] transition group-hover:bg-[#00B862] group-hover:text-white group-hover:shadow-[var(--shadow-glow)]">
          {iconMap[icon] ?? <ShieldCheck className="h-6 w-6" />}
        </span>
        <h3 className="mt-3 text-base font-semibold text-[#0A0A0A] xl:text-lg">
          <span className="apa-underline-reveal">{title}</span>
        </h3>
        <p className="mt-2 text-sm leading-6 text-[var(--color-muted)] xl:text-base">{text}</p>
      </div>
    </div>
  );
}

/** Icon card grid renderer with icons, titles and descriptions. */
export function IconCardGridRenderer({ component }: { component: IconCardGridComponent }) {
  const { items, cardsPerRowTablet, cardsPerRowDesktop, centerLastRow: centerLastRowOpt } = component.content;
  const centerLastRow = centerLastRowOpt === true || centerLastRowOpt === "true";
  const tablet = toPerRow(cardsPerRowTablet);
  const desktop = toPerRow(cardsPerRowDesktop);
  const gridCols = `sm:grid-cols-2 md:${COLS[tablet]} lg:${COLS[desktop]}`;
  const iconMap: Record<string, JSX.Element> = {
    ShieldCheck: <ShieldCheck className="h-6 w-6" />,
    Building2: <Building2 className="h-6 w-6" />,
    Banknote: <Banknote className="h-6 w-6" />,
    Database: <Database className="h-6 w-6" />,
    Target: <Target className="h-6 w-6" />,
    Gauge: <Gauge className="h-6 w-6" />,
    Zap: <Zap className="h-6 w-6" />,
  };

  if (!centerLastRow) {
    return (
      <section className="w-full">
        <div className={`grid w-full grid-flow-dense gap-4 ${gridCols} justify-items-start`}>
          {items.map(({ icon, title, text }, i) => (
            <Card key={`${title}-${i}`} icon={icon} title={title} text={text} iconMap={iconMap} />
          ))}
        </div>
      </section>
    );
  }

  const fullCount = Math.floor(items.length / desktop) * desktop;
  const fullRows = items.slice(0, fullCount);
  const lastRow = items.slice(fullCount);

  return (
    <section>
      {fullRows.length > 0 && (
        <div className={`grid gap-4 ${gridCols}`}>
          {fullRows.map(({ icon, title, text }, i) => (
            <Card key={`${title}-${i}`} icon={icon} title={title} text={text} iconMap={iconMap} />
          ))}
        </div>
      )}
      {lastRow.length > 0 && (
        <div
          className="icon-grid-partial-row mt-4 grid gap-4"
          style={{
            gridTemplateColumns: `repeat(${lastRow.length}, 1fr)`,
            maxWidth: `${(lastRow.length / desktop) * 100}%`,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {lastRow.map(({ icon, title, text }, i) => (
            <Card key={`${title}-${fullCount + i}`} icon={icon} title={title} text={text} iconMap={iconMap} />
          ))}
        </div>
      )}
    </section>
  );
}


